import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

import { allSharedComponents } from "../../../../../shared";
import { competitionActions } from "../../../../../redux/actions";
import { buttonTextHandler, getSportNameById } from "./../utilities";
import {
  TimeIcon,
  CalendarIcon,
  LogoPlaceholder,
  LocationIcon,
  AgeIcon,
} from "../../../../../assets/images/icon";
import {
  calculateVAT,
  dateToUTCWithTimezone,
  formatDateTimeRangeFromServer,
  precisionCalculation,
  serverFormatToMomentObject,
  toSingleDateStringIfEqual,
} from "../../../../../system/utility-functions";
import {
  Directions,
  competitionConstants,
  translationConstants,
  viewPathConstants,
  subscriptionTypeConstants,
  timezoneConstants,
  calculationOperations,
} from "../../../../../constants";

const { StopPropagation, ServerFormatToDate } = allSharedComponents;

const PublicCompetitionCard = ({
  data,
  flag,
  setSelectedCompetition,
  city,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [registrationPrice, setRegistrationPrice] = useState(0);

  const direction = useSelector((state) => {
    return state.LanguageChangeReducer.direction;
  });
  const subscriptionPrices = useSelector(
    (state) => state.SubscriptionReducer.subscriptionPrices
  );
  const sportType = getSportNameById(data?.sportId);

  const handleViewDetailClick = () => {
    dispatch(competitionActions.getPublicCompetitionById(data?.id));
    navigate(`${viewPathConstants.PUBLIC_COMPETITIONS_DETAIL}`, {
      state: { flag: flag, competitionId: data?.id },
    });
  };

  const navigateToLeaderboard = () => {
    navigate(`${viewPathConstants.COMPETITION_LEADERBOARD}`, {
      state: { competition: data },
    });
  };

  const isMobile = useSelector((state) => {
    return state.systemReducer.isMobile;
  });

  useEffect(() => {
    const vat = subscriptionPrices?.find(
      (x) => x.subcriptionType === subscriptionTypeConstants.VAT
    );
    if (data?.registrationFee && vat) {
      const totalPrice = precisionCalculation(
        calculateVAT(vat?.price, data?.registrationFee),
        data?.registrationFee,
        calculationOperations.addition
      );
      setRegistrationPrice(totalPrice);
    }
  }, [data, subscriptionPrices]);

  return (
    <div className="col-md-12 py-2">
      <div
        className="activity-card cast-shadow-on-hover"
        onClick={handleViewDetailClick}
      >
        <div className="row m-0 gap-3 gap-md-0 gap-md-0">
          <div className="col-md-1 col-lg-1 d-none d-md-none d-lg-block d-flex justify-content-md-center align-items-md-center px-1 text-md-center">
            <span className="activity-date" dir={Directions.ltr}>
              <ServerFormatToDate dateString={data?.startDateString} />
            </span>
          </div>
          <div className="col-12 col-md-10 col-lg-5">
            <div className="d-flex flex-wrap flex-sm-nowrap align-items-center justify-content-center justify-content-md-start gap-4">
              <div className="row w-100 align-items-center">
                <div className="col-5 col-md-4 col-lg-4 d-lg-flex justify-content-start">
                  <div className="competition-content-wrapper"></div>
                  <div
                    className={`activity-card-image-wrapper ms-md-2 ${
                      isMobile ? "h-100" : "h-100px"
                    }`}
                  >
                    <img
                      src={
                        data?.logo?.link ? data?.logo?.link : LogoPlaceholder
                      }
                      alt="activity"
                    />
                  </div>
                </div>
                <div className="col-7 col-md-5 col-lg-7 ps-3 d-flex flex-column justify-content-between">
                  <div className="d-flex flex-column ">
                    <span className="activity-card-heading text-ellipsis">
                      {direction === Directions.rtl && data?.arabicTitle
                        ? `${data?.arabicTitle}`
                        : `${data?.title}`}
                    </span>
                    <span className="font-16">
                      {`${t(translationConstants[sportType])}`}
                    </span>
                  </div>
                  <div>
                    <div className="col-12">
                      <div className="contact-information-card pt-md-2 d-flex gap-2 pt-0">
                        <img alt="" name="competitionSpot" src={LocationIcon} />
                        <span>
                          {direction === Directions.rtl
                            ? data?.spotInfo?.arabicSpotName ||
                              data?.spotInfo?.spotName
                            : data?.spotInfo?.spotName}
                          {direction === Directions.rtl
                            ? city?.arabicName || city?.name
                            : city?.name}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="contact-information-card pt-1 d-flex gap-2">
                        <img alt="" name="activityDate" src={CalendarIcon} />
                        <span>
                          {toSingleDateStringIfEqual(
                            data?.startDateString,
                            data?.endDateString
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex d-md-none flex-column">
                    <div className="row">
                      <div className="contact-information-card pt-1 d-flex gap-2">
                        <img alt="" name="activityDate" src={AgeIcon} />
                        <span>{` ${data?.minAge} - ${data?.maxAge} ${t(
                          translationConstants.YEARS
                        )}`}</span>
                      </div>
                    </div>
                    <div className="row">
                      <div className="contact-information-card pt-1 d-flex gap-2">
                        <img alt="" name="activityDate" src={TimeIcon} />
                        <span>
                          {formatDateTimeRangeFromServer(
                            data?.startDateString,
                            data?.endDateString,
                            timezoneConstants.hourMinuteFormat
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-12 col-lg-6 mt-md-0 gap-2 d-flex justify-content-between">
            <div className="col-5 col-md-5 col-lg-8 h-100 flex-wrap d-flex justify-content-between">
              <div className="col-md-7 col-12 d-flex flex-column justify-content-between">
                <div>
                  {flag === competitionConstants.UPCOMING &&
                    data?.isPublicRegistrationAllowed && (
                      <>
                        {!(
                          data?.registrationStartsInDays < 0 &&
                          data?.registrationEndsInDays < 0
                        ) &&
                          (dateToUTCWithTimezone().isBefore(
                            serverFormatToMomentObject(
                              data?.registrationStartDateString
                            )
                          ) ? (
                            <span
                              className={`timeLogDesign text-center text-md-start ${
                                isMobile ? "font-12" : "font-14"
                              }`}
                            >
                              {data?.registrationStartsInDays > 1
                                ? `${t(
                                    translationConstants.registration_starts_in
                                  )} ${data?.registrationStartsInDays} ${t(
                                    translationConstants.days
                                  )}`
                                : data?.registrationStartsInDays === 1
                                ? `${t(
                                    translationConstants.registration_starting_tomorrow
                                  )}`
                                : `${t(
                                    translationConstants.registration_starting_today
                                  )}`}
                            </span>
                          ) : (
                            <span
                              className={`timeLogDesign text-center text-md-start ${
                                isMobile ? "font-10" : "font-14"
                              }`}
                            >
                              {data?.registrationEndsInDays > 1
                                ? `${t(
                                    translationConstants.registration_ending_in
                                  )} ${data?.registrationEndsInDays} ${t(
                                    translationConstants.days
                                  )}`
                                : data?.registrationEndsInDays === 1
                                ? `${t(
                                    translationConstants.registration_ending_tomorrow
                                  )}`
                                : `${t(
                                    translationConstants.registration_ending_today
                                  )}`}
                            </span>
                          ))}
                      </>
                    )}
                </div>
                <div className="d-none d-md-flex flex-column">
                  <div className="row">
                    <div className="contact-information-card pt-1 d-flex gap-2">
                      <img alt="" name="activityDate" src={AgeIcon} />
                      <span>{` ${data?.minAge} - ${data?.maxAge} ${t(
                        translationConstants.YEARS
                      )}`}</span>
                    </div>
                  </div>
                  <div className="row">
                    <div className="contact-information-card pt-1 d-flex gap-2">
                      <img alt="" name="activityDate" src={TimeIcon} />
                      <span>
                        {formatDateTimeRangeFromServer(
                          data?.startDateString,
                          data?.endDateString,
                          timezoneConstants.hourMinuteFormat
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-5 col-12 d-flex flex-column">
                {flag === competitionConstants.UPCOMING &&
                  !(
                    data?.registrationStartsInDays < 0 &&
                    data?.registrationEndsInDays < 0
                  ) && (
                    <>
                      {data?.maximumParticipantsAllowed > 0 &&
                        data?.isPublicRegistrationAllowed && (
                          // !registrationClosed &&
                          <div
                            className={`seatsAvailableCard ${
                              isMobile ? "w-100" : "w-95px"
                            } px-2 mx-md-3 mx-0`}
                          >
                            <h1 className="m-0 p-0 fw-700">
                              {data?.seatsAvailable}
                            </h1>
                            <h6 className="font-12 text-center">
                              {t(translationConstants.SEATS_AVAILABLE)}
                            </h6>
                          </div>
                        )}
                    </>
                  )}
              </div>
            </div>
            <div className="col-7 col-md-5 col-lg-4 d-flex justify-content-center align-items-center">
              <div className="d-flex flex-column align-items-between justify-content-center gap-1">
                {flag === competitionConstants.UPCOMING ? (
                  !dateToUTCWithTimezone().isSameOrAfter(
                    serverFormatToMomentObject(data?.registrationEndDateString)
                  ) ? (
                    <>
                      {data?.isPublicRegistrationAllowed &&
                        flag === competitionConstants.UPCOMING &&
                        !(
                          data?.registrationStartsInDays < 0 &&
                          data?.registrationEndsInDays < 0
                        ) && (
                          <>
                            <div className="d-flex flex-column gap-1 text-center">
                              {data?.registrationFee !== null && (
                                <>
                                  <h6 className="font-14 m-0 p-0">
                                    {t(translationConstants.registration_fees)}
                                  </h6>
                                  <h5 className="font-14 fw-bold m-0">
                                    {translationConstants.SAR}{" "}
                                    {registrationPrice}
                                  </h5>
                                  {data?.specialMembersDiscount !== null && (
                                    <p className="font-10">
                                      {`${Math.trunc(
                                        data?.specialMembersDiscount
                                      )}${t(
                                        translationConstants.DISCOUNT_FOR_SSF_MEMBERS
                                      )}`}
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        )}
                      <StopPropagation>
                        <Button
                          className={
                            !data?.isPublicRegistrationAllowed ||
                            data?.seatsAvailable <= 0
                              ? "registration-invite-only"
                              : dateToUTCWithTimezone().isSameOrAfter(
                                  serverFormatToMomentObject(
                                    data?.registrationStartDateString
                                  )
                                )
                              ? "registration-ending"
                              : "registration-openning"
                          }
                          disabled={
                            data?.seatsAvailable <= 0 ||
                            dateToUTCWithTimezone().isBefore(
                              serverFormatToMomentObject(
                                data?.registrationStartDateString
                              )
                            ) ||
                            !data?.isPublicRegistrationAllowed
                          }
                          onClick={() => {
                            setSelectedCompetition(data?.id);
                          }}
                        >
                          {buttonTextHandler(t, data)}
                        </Button>
                      </StopPropagation>
                    </>
                  ) : (
                    <span className="underline">
                      {t(translationConstants.REGISTRATION_CLOSED)}
                    </span>
                  )
                ) : data?.isScorePublished ? (
                  <StopPropagation>
                    <Button
                      className="btn-submit-outline m-0"
                      onClick={() => {
                        navigateToLeaderboard();
                      }}
                    >
                      {t(translationConstants.VIEW_RESULT)}
                    </Button>
                  </StopPropagation>
                ) : (
                  <StopPropagation>
                    <Button
                      className="registration-invite-only m-0 customBtnforCompetionInProgress"
                      disabled={true}
                    >
                      {t(translationConstants.SCORE_NOT_PUBLISHED)}
                    </Button>
                  </StopPropagation>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCompetitionCard;
